// ===================================================
// ФАЙЛ: frontend/src/utils/axios.js
// УНИФИЦИРОВАННЫЙ HTTP КЛИЕНТ - С ПОДДЕРЖКОЙ ДОМЕНОВ
// ===================================================
import axios from 'axios';
import { toastAPI } from '../contexts/ToastContext';
import { API_ENDPOINTS, STORAGE_KEYS } from 'utils/constants';
import { pickSafeRussianBackendMessage } from 'utils/safeRussianBackendMessage';
import {
  trySurfaceShellHttpError,
  getApiErrorToastBody,
} from 'utils/shellHttpErrorSurface';
import { isLoopbackHost } from 'utils/loopbackHost';
import { resolveMarkbaseProductionAppHostname } from 'utils/markbaseSpaHosts';
import { billingApiPath } from './billingApiPaths';
import { reportSessionUnauthorized } from './sessionGuard';

// =====================================
// КОНФИГУРАЦИЯ API URL
// ===================================================
/**
 * Страница на https, а в REACT_APP_API_URL иногда остаётся http://тот-же-host (docker/старые env).
 * Тогда запрос идёт на http://app... — для CSP это не 'self' и зона connect-src не содержит http-оригин.
 */
const isMarkbaseManagedHost = (hostname) => {
  const h = String(hostname || '').toLowerCase();
  return h === 'markbase.ru' || h.endsWith('.markbase.ru');
};

const alignApiOriginWithPage = (baseUrl) => {
  if (typeof window === 'undefined' || !baseUrl) return baseUrl;
  try {
    const pageIsHttps = window.location.protocol === 'https:';
    if (!pageIsHttps) return baseUrl;
    const u = new URL(String(baseUrl), window.location.origin);
    if (u.protocol !== 'http:') return baseUrl;
    const pageHost = window.location.hostname;
    if (u.hostname === pageHost || isMarkbaseManagedHost(u.hostname)) {
      u.protocol = 'https:';
      return u.origin;
    }
  } catch (_) {}
  return baseUrl;
};

// Определяем базовый URL API в зависимости от домена
const getApiBaseUrl = () => {
  if (typeof window === 'undefined') {
    return 'http://127.0.0.1:8080';
  }

  const protocol = window.location.protocol;
  const hostname = (window.location.hostname || '').toLowerCase();

  // Loopback (127.0.0.1 / ::1): тот же origin или REACT_APP_API_URL ниже
  if (isLoopbackHost(hostname)) {
    return alignApiOriginWithPage(window.location.origin);
  }

  // Любая HTTPS-страница в проде: весь /api/* отдаётся с того же хоста (nginx app.markbase.conf).
  // Для *.markbase.ru baseURL оставляем пустым: axios тогда не склеивает http:// из env/build,
  // а отдаёт в XHR только путь `/api/...` — схема всегда как у документа (HTTPS), без CSP connect-src на http.
  if (protocol === 'https:') {
    if (isMarkbaseManagedHost(hostname)) {
      return '';
    }
    return window.location.origin;
  }

  // ✅ КРИТИЧЕСКОЕ ИСПРАВЛЕНИЕ: ВСЕГДА используем текущий домен для API
  // API всегда доступен через /api/ на текущем домене благодаря nginx reverse proxy
  // Это предотвращает проблемы с CORS и гарантирует правильную работу
  const currentOrigin = alignApiOriginWithPage(`${protocol}//${window.location.hostname}`);

  // ✅ Если задана переменная окружения - проверяем, но предпочитаем текущий домен
  if (process.env.REACT_APP_API_URL) {
    try {
      const envUrl = process.env.REACT_APP_API_URL.replace(/\/+$/, '').replace(/\/api$/, '');
      const envHostname = new URL(envUrl).hostname.toLowerCase();

      // ✅ Если переменная окружения указывает на другой домен - игнорируем её
      // и используем текущий домен для избежания CORS проблем
      if (envHostname !== hostname && !isLoopbackHost(hostname)) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            `⚠️ REACT_APP_API_URL указывает на другой домен (${envHostname}), используем текущий (${hostname}), чтобы избежать CORS`
          );
        }
        return alignApiOriginWithPage(currentOrigin);
      }

      // Если домен совпадает - можно использовать envUrl
      return alignApiOriginWithPage(envUrl);
    } catch (e) {
      console.warn('⚠️ Некорректный REACT_APP_API_URL, используем текущий домен:', e.message);
      return alignApiOriginWithPage(currentOrigin);
    }
  }

  // По умолчанию используем текущий домен
  return alignApiOriginWithPage(currentOrigin);
};

// ✅ КРИТИЧЕСКОЕ ИСПРАВЛЕНИЕ: Безопасная инициализация API_BASE_URL
// с защитой от ошибок инициализации при циклических зависимостях
let API_BASE_URL;
try {
  // getApiBaseUrl уже прогоняет alignApiOriginWithPage (https-страница + http в env)
  API_BASE_URL = getApiBaseUrl();
  // ✅ ДИАГНОСТИКА: лог только в development
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    console.log('🔍 Базовый URL API:', API_BASE_URL);
    console.log('🔍 Текущий origin:', window.location.origin);
    console.log('🔍 REACT_APP_API_URL:', process.env.REACT_APP_API_URL || '(не задан)');
  }
} catch (error) {
  // Fallback на текущий origin если инициализация не удалась
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.warn('⚠️ Ошибка инициализации API_BASE_URL, используем запасной origin:', error);
  }
  API_BASE_URL = alignApiOriginWithPage(
    typeof window !== 'undefined' ? window.location.origin : 'http://127.0.0.1:8080'
  );
}

/* Повторное выравнивание: старые сборки / env с http://app на https-странице → иначе CSP connect-src блокирует */
if (typeof window !== 'undefined' && String(API_BASE_URL || '').trim() !== '') {
  API_BASE_URL = alignApiOriginWithPage(String(API_BASE_URL).replace(/\/+$/, ''));
}

const REQUEST_TIMEOUT = 30000;

let refreshRequest = null;

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT,
  withCredentials: true, // Отправляем httpOnly cookie (uam_session) для кросс-доменных запросов
  headers: {
    'Content-Type': 'application/json',
  },
});

// CSP connect-src на app.markbase.ru не содержит http:// — при «залипшем» http в baseURL или абсолютном url запрос блокируется.
// В части версий axios в перехватчике config.baseURL бывает пустым, а host остаётся только в instance.defaults.baseURL.
const attachHttpsPageOriginInterceptor = (instance) => {
  instance.interceptors.request.use((config) => {
    /**
     * MPLAZA только на app-shell. Относительный /api/mplaza/* с http baseURL → http в XHR (CSP).
     * Принудительно канонический https://app.markbase.ru + пустой base (кросс-поддомен: логистика → app).
     */
    if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
      try {
        let rawUrl = String(config.url || '');
        if (rawUrl.startsWith('/api/mplaza/') && !/^https?:\/\//i.test(rawUrl)) {
          rawUrl = new URL(rawUrl, 'https://app.markbase.ru').href;
          config.url = rawUrl;
          config.baseURL = '';
          instance.defaults.baseURL = '';
        }
      } catch (_) { /* noop */ }
    }
    /* Любая страница: жёстко убираем http://app — в CSP connect-src только https://app.markbase.ru */
    if (typeof window !== 'undefined') {
      try {
        const ru = String(config.url || '');
        if (/^http:\/\/app\.markbase\.ru/i.test(ru)) {
          config.url = ru.replace(/^http:\/\//i, 'https://');
        }
        const b = String(config.baseURL || '');
        if (/^http:\/\/app\.markbase\.ru/i.test(b)) {
          config.baseURL = b.replace(/^http:\/\//i, 'https://');
        }
        const db = String(instance.defaults.baseURL || '');
        if (/^http:\/\/app\.markbase\.ru/i.test(db)) {
          instance.defaults.baseURL = db.replace(/^http:\/\//i, 'https://');
        }
      } catch (_) { /* noop */ }
    }
    if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
      try {
        const ru = String(config.url || '');
        // Явный http://app на https-странице — режем до пути (CSP connect-src без http)
        if (/^http:\/\/app\.markbase\.ru/i.test(ru)) {
          const u = new URL(ru);
          config.url = `${u.pathname}${u.search}${u.hash}`;
          const pageHost = window.location.hostname;
          const sameBase = isMarkbaseManagedHost(pageHost) ? '' : window.location.origin.replace(/\/+$/, '');
          config.baseURL = sameBase;
          instance.defaults.baseURL = sameBase;
        }
      } catch (_) { /* noop */ }
    }
    if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
      try {
        const pageHost = window.location.hostname;
        const pageHostLc = (pageHost || '').toLowerCase();
        const shouldUpgradeHttp = (h) =>
          (h || '').toLowerCase() === pageHostLc || isMarkbaseManagedHost(h);

        // Самое раннее: полный URL http://<тот же host, что у вкладки>/... → относительный путь + https origin.
        // Иначе ряд сборок/плагинов оставляет http в абсолютном url → connect-src без http, XHR блокируется, в консоли «violates CSP».
        if (config.url && /^http:\/\//i.test(String(config.url))) {
          try {
            const absEarly = new URL(String(config.url));
            if (absEarly.hostname.toLowerCase() === pageHostLc) {
              config.url = `${absEarly.pathname}${absEarly.search}${absEarly.hash}`;
              const sameBase = isMarkbaseManagedHost(pageHost) ? '' : window.location.origin.replace(/\/+$/, '');
              config.baseURL = sameBase;
              instance.defaults.baseURL = sameBase;
            }
          } catch (_) { /* noop */ }
        }

        // Без схемы //host/path на https-странице часто превращается в http://host → CSP connect-src + mixed content
        if (config.url && String(config.url).startsWith('//')) {
          try {
            const pu = new URL(`https:${config.url}`);
            if (shouldUpgradeHttp(pu.hostname)) {
              config.url = `${pu.pathname}${pu.search}${pu.hash}`;
              const o = isMarkbaseManagedHost(pageHost) ? '' : window.location.origin.replace(/\/+$/, '');
              config.baseURL = o;
              instance.defaults.baseURL = o;
            }
          } catch (_) { /* noop */ }
        }

        // Жёстко: на *.markbase.ru относительные /api/* — всегда с текущего https-origin
        // (старые сборки с http в REACT_APP_API_URL или в defaults).
        if (isMarkbaseManagedHost(pageHost)) {
          const rel = config.url;
          const isAbsolute = rel && /^https?:\/\//i.test(String(rel));
          if (!isAbsolute) {
            const o = '';
            config.baseURL = o;
            if (String(instance.defaults.baseURL || '').replace(/\/+$/, '') !== o) {
              instance.defaults.baseURL = o;
            }
          }
        }

        const rawBaseCandidate = (() => {
          const fromReq = config.baseURL != null && String(config.baseURL).trim() !== ''
            ? String(config.baseURL)
            : '';
          if (fromReq) return fromReq.replace(/\/+$/, '');
          const fromDefaults = String(instance.defaults.baseURL || '').trim();
          return fromDefaults.replace(/\/+$/, '');
        })();

        if (rawBaseCandidate.startsWith('http://')) {
          const u = new URL(rawBaseCandidate);
          if (shouldUpgradeHttp(u.hostname)) {
            const fixedBase = isMarkbaseManagedHost(pageHost)
              ? ''
              : (() => {
                  u.protocol = 'https:';
                  return u.origin;
                })();
            config.baseURL = fixedBase;
            if (String(instance.defaults.baseURL || '').startsWith('http://')) {
              instance.defaults.baseURL = fixedBase;
            }
          }
        }

        const rel = config.url;
        if (rel && /^https?:\/\//i.test(String(rel))) {
          const u = new URL(String(rel));
          if (u.protocol === 'http:' && shouldUpgradeHttp(u.hostname)) {
            const targetHost = (u.hostname || '').toLowerCase();
            const isAppShellHost = targetHost === 'app.markbase.ru';
            if (isMarkbaseManagedHost(pageHost) && isAppShellHost && pageHostLc !== targetHost) {
              u.protocol = 'https:';
              config.url = u.toString();
            } else {
              u.protocol = 'https:';
              config.url = `${u.pathname}${u.search}${u.hash}`;
              const b = String(config.baseURL || instance.defaults.baseURL || '');
              if (!b || b.startsWith('http://')) {
                const newBase = isMarkbaseManagedHost(pageHost) ? '' : u.origin;
                config.baseURL = newBase;
                if (String(instance.defaults.baseURL || '').startsWith('http://')) {
                  instance.defaults.baseURL = newBase;
                }
              }
            }
          }
        }
        // Mixed Content: итоговая страховка — никогда не оставляем http:// для того же host, что и у страницы
        {
          const po = window.location.origin;
          const fixSameHostHttpBase = (raw) => {
            const s = String(raw || '').trim().replace(/\/+$/, '');
            if (!s.startsWith('http://')) return null;
            try {
              const u = new URL(s);
              if (u.hostname.toLowerCase() === pageHostLc) {
                return isMarkbaseManagedHost(pageHost) ? '' : po;
              }
            } catch (_) {}
            return null;
          };
          const fb = fixSameHostHttpBase(config.baseURL);
          if (fb !== null && fb !== undefined) config.baseURL = fb;
          const fd = fixSameHostHttpBase(instance.defaults.baseURL);
          if (fd !== null && fd !== undefined) instance.defaults.baseURL = fd;
        }
      } catch (_) { /* noop */ }
    }
    return config;
  });
};
attachHttpsPageOriginInterceptor(axiosInstance);

/** Выравнивание defaults.baseURL под HTTPS-страницу (основной и дочерние axios-инстансы). */
function syncAxiosDefaultsBaseToHttps(instance) {
  if (typeof window === 'undefined' || window.location.protocol !== 'https:') return;
  try {
    const b = String(instance.defaults.baseURL || '').replace(/\/+$/, '');
    if (b.startsWith('http://')) {
      const u = new URL(b);
      if (u.hostname === window.location.hostname || isMarkbaseManagedHost(u.hostname)) {
        if (isMarkbaseManagedHost(window.location.hostname)) {
          instance.defaults.baseURL = '';
        } else {
          u.protocol = 'https:';
          instance.defaults.baseURL = u.origin;
        }
      }
    }
  } catch (_) { /* noop */ }
}
syncAxiosDefaultsBaseToHttps(axiosInstance);

/** На боевом *.markbase.ru по HTTPS — только относительные пути (без baseURL), схема как у документа; залипший http в env не попадает в XHR. */
if (typeof window !== 'undefined' && window.location.protocol === 'https:' && isMarkbaseManagedHost(window.location.hostname)) {
  axiosInstance.defaults.baseURL = '';
}

// =====================================
// ДЕДУПЛИКАТОР GET-ЗАПРОСОВ + IN-MEMORY КЭШ
// =====================================
// ✅ Цель: не дергать одинаковые GET при каждом клике/рендере.
// По умолчанию — короткий TTL от "дребезга", но можно задать долгий TTL per-request (cacheTtlMs)
// и получить поведение "загрузили и держим до перезагрузки страницы".
//
// Map в JS гарантирует insertion order → используем как FIFO для O(1) эвикции
// (oldest = `cache.keys().next().value`). Старая реализация делала линейный
// проход по всем 500 entries при каждой эвикции — под нагрузкой это давало
// заметную CPU-нагрузку.
const inflightRequests = new Map(); // key -> { promise: Promise<AxiosResponse>, startedAt: number }
const responseCache = new Map(); // key -> { ts: number, ttlMs: number, response }
// 15 сек — баланс «свежесть данных vs. фоновые повторы». Раньше было 2с, и
// большинство «обычных» GET (вне NO_CACHE_REGEX) повторно били в сеть на каждой
// перерисовке header/sidebar/widgets — типичное «думает 1-2 сек на каждое
// действие». Эндпоинты с реально живыми данными остаются в NO_CACHE_REGEX
// (auth/me, prices, stock, sync stock и т.д.); им TTL=15с не помешает.
const DEFAULT_RESPONSE_CACHE_TTL_MS = 15000;
const MAX_RESPONSE_CACHE_ENTRIES = 500; // защита памяти (таблицы/страницы) - увеличено для большего кэша
// Если запрос "повис" дольше таймаута, считаем его устаревшим
const INFLIGHT_STALE_MS = REQUEST_TIMEOUT + 5000;
// Жёсткая граница на регистр in-flight: при быстрой навигации (десятки переходов
// в секунду + автополлинг виджетов) Map бесконтрольно рос. Совпадает с MAX_RESPONSE_CACHE_ENTRIES
// по той же логике (одна точка контроля памяти).
const MAX_INFLIGHT_ENTRIES = 500;

const buildKey = (url, config = {}) => {
  const method = (config.method || 'get').toLowerCase();
  const params = config.params ? JSON.stringify(Object.keys(config.params).sort().reduce((acc, k) => { acc[k] = config.params[k]; return acc; }, {})) : '';
  return `${method}::${url}::${params}`;
};

// =====================================
// УМНАЯ СИСТЕМА КЭШИРОВАНИЯ
// Автоматическое определение TTL на основе типа данных
// =====================================
// Скомпилированные регексы — один раз на модульный scope. Раньше new RegExp(...)
// вызывался на каждый GET (× десятки запросов в секунду на дашборде), плюс на
// каждый mutation-response в interceptor cleanup. Кумулятивно это давало
// сотни ms CPU-времени на типичной сессии.
const compilePatterns = (patterns) =>
  patterns.map((p) => new RegExp(p.replace(/\*/g, '.*')));

const NO_CACHE_REGEX = compilePatterns([
  '/api/auth/me',
  // Импорт файлов: списки конфигов/файлов не должны залипать в 15s GET-кэше shell axios
  '/api/file-import/',
  '/api/mplaza/v1/agreements',
  '/api/prices',
  '/api/products/bulk-fields',
  '/api/suppliers/.*/products/bulk-fields',
  '/api/warehouses/.*/stock',
  '/api/sync/stock',
  '/api/products.*stock',
  '/api/stocks',
  '/api/warehouses.*stock',
  '/api/suppliers.*stocks',
  '/api/suppliers.*prices',
  '/api/harvest.*prices',
  '/api/harvest.*stocks',
]);

const LONG_CACHE_REGEX = compilePatterns([
  '/api/dictionaries/',
  // import-rules НЕ кэшируем: иначе после PUT пользователь до 5 минут видит старые правила
  '/api/settings/companies/.*/theme-colors',
  '/api/settings/companies/.*/resource-styles',
  '/api/admin/public/theme-colors',
  '/api/admin/public/resource-styles',
  '/api/users/favorites/menu',
  '/api/analytics/v1/dashboard-settings',
  '/api/seo/meta/',
  '/api/connected-companies',
]);

const MEDIUM_CACHE_REGEX = compilePatterns([
  '/api/users',
  '/api/companies',
  '/api/marketplaces',
  '/api/warehouses$',
  '/api/suppliers$',
  '/api/products/categories',
  billingApiPath('balance'),
  billingApiPath('resources-usage'),
  billingApiPath('company-billing-info'),
  '/api/currencies',
  '/api/sync/status',
]);

const SHORT_CACHE_REGEX = compilePatterns([
  '/api/analytics/dashboard',
  '/api/orders',
  '/api/products',
  '/api/admin/monitoring',
  '/api/user-resources/processes/active',
  '/api/notifications/unread-count',
]);

// preserve-патерны для очистки кэша после mutation (response interceptor).
const PRESERVE_AFTER_MUTATION_REGEX = compilePatterns([
  '/api/dictionaries/',
  '/api/settings/companies/.*/theme-colors',
  '/api/settings/companies/.*/resource-styles',
  '/api/admin/public/theme-colors',
  '/api/admin/public/resource-styles',
  '/api/users/favorites/menu',
  '/api/analytics/v1/dashboard-settings',
]);

const getCacheTTL = (url, config = {}) => {
  // Если cacheTtlMs явно указан - используем его
  if (config?.cacheTtlMs !== undefined) {
    return config.cacheTtlMs === 0 ? 0 : config.cacheTtlMs;
  }

  const urlStr = String(url || '').toLowerCase();

  // ❌ НЕ КЭШИРУЕМ: Цены и наличие (критически важные данные)
  if (NO_CACHE_REGEX.some((re) => re.test(urlStr))) return 0;

  // ✅ ДОЛГИЙ КЭШ (5 минут): Справочники и статические данные
  if (LONG_CACHE_REGEX.some((re) => re.test(urlStr))) return 5 * 60 * 1000;

  // ✅ СРЕДНИЙ КЭШ (2 минуты): Настройки и конфигурация
  // ❌ НЕ КЭШИРУЕМ: /api/settings - настройки НДС и режим ввода цен должны быть актуальными
  if (MEDIUM_CACHE_REGEX.some((re) => re.test(urlStr))) return 2 * 60 * 1000;

  // ❌ НЕ КЭШИРУЕМ: Настройки компании (НДС, режим ввода цен) - критически важные данные
  if (urlStr.includes('/api/settings')) return 0;

  // ✅ КОРОТКИЙ КЭШ (30 секунд): Динамические данные
  if (SHORT_CACHE_REGEX.some((re) => re.test(urlStr))) return 30 * 1000;

  return DEFAULT_RESPONSE_CACHE_TTL_MS; // Default 2 seconds
};

const originalGet = axiosInstance.get.bind(axiosInstance);

axiosInstance.get = (url, config = {}) => {
  // Флаг для отключения дедупликации по месту вызова
  if (config && config.noDedup === true) {
    return originalGet(url, config);
  }

  // Предзагрузки: не должны блокировать реальные запросы
  const isPrefetch = config && config.prefetch === true;

  const key = buildKey(url, { ...config, method: 'get' });
  const now = Date.now();

  // Используем умную систему кэширования, если cacheTtlMs не указан явно
  const ttlMsRaw = config?.cacheTtlMs !== undefined
    ? config.cacheTtlMs
    : getCacheTTL(url, config);
  const ttlMs = (ttlMsRaw === Infinity)
    ? Infinity
    : (Number.isFinite(Number(ttlMsRaw)) ? Math.max(0, Number(ttlMsRaw)) : DEFAULT_RESPONSE_CACHE_TTL_MS);

  // In-memory кэш ответа
  const cached = responseCache.get(key);
  if (cached) {
    const effectiveTtl = (cached.ttlMs === Infinity) ? Infinity : (Number(cached.ttlMs) || 0);
    const fresh = (effectiveTtl === Infinity) ? true : (now - cached.ts < effectiveTtl);
    if (fresh) return Promise.resolve(cached.response);
    responseCache.delete(key);
  }

  // Дедупликация параллельных запросов
  if (!isPrefetch && inflightRequests.has(key)) {
    const inflight = inflightRequests.get(key);
    const startedAt = Number(inflight?.startedAt || 0);
    if (startedAt && (now - startedAt) < INFLIGHT_STALE_MS) {
      return inflight.promise;
    }
    // stale запрос — удаляем и выполняем новый
    inflightRequests.delete(key);
  }

  const p = originalGet(url, config)
    .then((resp) => {
      // ttlMs=0 означает "не кэшировать"
      if (ttlMs !== 0) {
        // Если ключ уже есть — удалим, чтобы set положил его в конец Map
        // (insertion order = LRU-ish: «свежее обращение → дальше от выселения»).
        if (responseCache.has(key)) responseCache.delete(key);
        responseCache.set(key, { ts: Date.now(), ttlMs, response: resp });
        // O(1) FIFO эвикция: Map хранит порядок вставки, oldest = first key.
        // Раньше тут был линейный скан 500 entries — лишняя CPU нагрузка
        // под параллельной нагрузкой дашбордов / автополлинга виджетов.
        while (responseCache.size > MAX_RESPONSE_CACHE_ENTRIES) {
          const oldestKey = responseCache.keys().next().value;
          if (oldestKey === undefined) break;
          responseCache.delete(oldestKey);
        }
      }
      inflightRequests.delete(key);
      return resp;
    })
    .catch((err) => {
      inflightRequests.delete(key);
      throw err;
    });

  if (!isPrefetch) {
    // Bound: при быстрой навигации + параллельных pollers Map бесконтрольно
    // рос (даже несмотря на cleanup в .then/.catch — бывали аномалии).
    while (inflightRequests.size >= MAX_INFLIGHT_ENTRIES) {
      const oldestKey = inflightRequests.keys().next().value;
      if (oldestKey === undefined) break;
      inflightRequests.delete(oldestKey);
    }
    inflightRequests.set(key, { promise: p, startedAt: now });
  }
  return p;
};

// Ручная очистка кэша (полезно для сложных сценариев)
axiosInstance.clearGetCache = (matcher = null) => {
  try {
    if (!matcher) {
      responseCache.clear();
      inflightRequests.clear();
      return;
    }
    const matchFn = (typeof matcher === 'function')
      ? matcher
      : (k) => String(k).includes(String(matcher));
    for (const k of Array.from(responseCache.keys())) {
      if (matchFn(k)) responseCache.delete(k);
    }
    for (const k of Array.from(inflightRequests.keys())) {
      if (matchFn(k)) inflightRequests.delete(k);
    }
  } catch (_) {}
};

// =====================================
// HELPERS
// =====================================
const hasStorage = (s) => {
  try {
    return typeof window !== 'undefined' && s && typeof s.getItem === 'function';
  } catch (_) {
    return false;
  }
};

const getStorage = (persist = true) => {
  if (typeof window === 'undefined') return null;
  const preferred = persist ? window.localStorage : window.sessionStorage;
  if (hasStorage(preferred)) return preferred;
  const fallback = persist ? window.sessionStorage : window.localStorage;
  return hasStorage(fallback) ? fallback : null;
};

const getToken = () => {
  const ss = hasStorage(window?.sessionStorage) ? window.sessionStorage : null;
  const ls = hasStorage(window?.localStorage) ? window.localStorage : null;
  return (ss?.getItem(STORAGE_KEYS.AUTH_TOKEN) || ss?.getItem('accessToken') || ss?.getItem('token'))
    || (ls?.getItem(STORAGE_KEYS.AUTH_TOKEN) || ls?.getItem('accessToken') || ls?.getItem('token'));
};

const getRefreshToken = () => {
  const ss = hasStorage(window?.sessionStorage) ? window.sessionStorage : null;
  const ls = hasStorage(window?.localStorage) ? window.localStorage : null;
  return (ss?.getItem(STORAGE_KEYS.REFRESH_TOKEN) || ss?.getItem('refreshToken'))
    || (ls?.getItem(STORAGE_KEYS.REFRESH_TOKEN) || ls?.getItem('refreshToken'));
};

const setTokens = (token, refreshToken = null, options = {}) => {
  if (!token) return;
  const persist = options && Object.prototype.hasOwnProperty.call(options, 'persist') ? !!options.persist : true;
  const storage = getStorage(persist);
  if (!storage) return;

  storage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
  // Backward compatibility
  storage.setItem('accessToken', token);
  storage.setItem('token', token);
  if (refreshToken) {
    storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    storage.setItem('refreshToken', refreshToken);
  }
};

const clearTokens = () => {
  const ss = hasStorage(window?.sessionStorage) ? window.sessionStorage : null;
  const ls = hasStorage(window?.localStorage) ? window.localStorage : null;
  [ss, ls].filter(Boolean).forEach((s) => {
    s.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    s.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    s.removeItem(STORAGE_KEYS.USER_DATA);
    s.removeItem(STORAGE_KEYS.SESSION_ID);
    s.removeItem('accessToken');
    s.removeItem('token');
    s.removeItem('refreshToken');
    s.removeItem('user');
  });
};

const getSessionId = () => {
  const ss = hasStorage(window?.sessionStorage) ? window.sessionStorage : null;
  const ls = hasStorage(window?.localStorage) ? window.localStorage : null;
  return (ss?.getItem(STORAGE_KEYS.SESSION_ID))
    || (ls?.getItem(STORAGE_KEYS.SESSION_ID))
    || null;
};

const setSessionId = (sessionId, options = {}) => {
  if (!sessionId) return;
  const persist = options && Object.prototype.hasOwnProperty.call(options, 'persist') ? !!options.persist : true;
  const storage = getStorage(persist);
  if (!storage) return;
  storage.setItem(STORAGE_KEYS.SESSION_ID, String(sessionId));
};

// Кэш разобранных JWT (token → payload). Раньше каждый запрос декодировал
// токен дважды (atob + JSON.parse в getToken и getTenantIdFromToken); на
// странице товаров с десятками параллельных GET это давало ощутимую CPU-нагрузку.
// Кэш по самому token-string: при смене токена старый ключ выпадает естественно;
// размер ограничиваем (обычно 1-2 живых токена за сессию).
const _decodedTokenCache = new Map();
const _DECODED_TOKEN_CACHE_MAX = 8;

const decodeToken = (token) => {
  if (!token) return null;
  const cached = _decodedTokenCache.get(token);
  if (cached !== undefined) return cached;
  try {
    const parts = String(token).split('.');
    if (parts.length < 2) {
      _decodedTokenCache.set(token, null);
      return null;
    }
    let payload = parts[1];
    payload = payload.replace(/-/g, '+').replace(/_/g, '/');
    const pad = payload.length % 4;
    if (pad) payload += '='.repeat(4 - pad);
    const decoded = atob(payload);
    const parsed = JSON.parse(decoded);
    if (_decodedTokenCache.size >= _DECODED_TOKEN_CACHE_MAX) {
      const oldest = _decodedTokenCache.keys().next().value;
      if (oldest !== undefined) _decodedTokenCache.delete(oldest);
    }
    _decodedTokenCache.set(token, parsed);
    return parsed;
  } catch (e) {
    console.warn('Ошибка разбора токена:', e);
    _decodedTokenCache.set(token, null);
    return null;
  }
};

const isTokenExpired = (token) => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  const currentTime = Math.floor(Date.now() / 1000);
  return decoded.exp < currentTime;
};

const getTenantIdFromToken = (token) => {
  const decoded = decodeToken(token);
  return decoded?.companyId || decoded?.company_id || decoded?.tenant?.companyId || decoded?.tenant?.company_id || null;
};

// =====================================
// INTERCEPTORS
// =====================================
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      const companyId = getTenantIdFromToken(token);
      if (companyId) config.headers['X-Tenant-ID'] = companyId;
    }

    const sessionId = getSessionId();
    if (sessionId) {
      config.headers['X-Session-Id'] = sessionId;
    }

    // После переключения активной компании CompanySwitcher выставляет
    // sessionStorage-флаг с дедлайном (~15 сек). Пока он жив — просим backend
    // обойти 5-секундный кэш company-context (см. _shared/auth/markbase-company-context.js
    // и FILES auth.middleware.js). Это закрывает race в окне между axios.clearGetCache()
    // и физическим page reload.
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        const raw = window.sessionStorage.getItem('mb:companyContextRefreshUntil');
        const until = raw ? Number(raw) : 0;
        if (until && Number.isFinite(until) && Date.now() < until) {
          config.headers['X-Refresh-Company-Context'] = '1';
        } else if (until) {
          window.sessionStorage.removeItem('mb:companyContextRefreshUntil');
        }
      }
    } catch (_) { /* sessionStorage может быть недоступен — это не критично */ }

    // ✅ ИСПРАВЛЕНО: Для FormData не устанавливаем Content-Type - браузер установит автоматически с boundary
    // Если это FormData, удаляем явно установленный Content-Type, чтобы браузер установил правильный
    if (config.data instanceof FormData) {
      // Удаляем Content-Type, если он был установлен - браузер сам установит multipart/form-data с boundary
      delete config.headers['Content-Type'];
      // Также удаляем из transformRequest, если там есть
      if (config.transformRequest && Array.isArray(config.transformRequest)) {
        // transformRequest уже обрабатывает FormData правильно
      }
    }

    // Dev-трейс HTTP — за явным флагом window.__MB_AXIOS_TRACE__.
    // Раньше каждый запрос всегда печатал в DevTools полный body/data; на
    // странице товаров с большими payload это съедало 50-200мс на запрос
    // (сериализация в DevTools). По умолчанию выключено.
    if (
      process.env.NODE_ENV === 'development'
      && typeof window !== 'undefined'
      && window.__MB_AXIOS_TRACE__ === true
    ) {
      // eslint-disable-next-line no-console
      console.log(`🚀 ${config.method?.toUpperCase()} ${config.url}`);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ✅ ИСПРАВЛЕНО: Функция для определения нужен ли редирект на login
const shouldRedirectToLogin = () => {
  if (typeof window === 'undefined') return false;

  const hostname = window.location.hostname;
  const pathname = window.location.pathname;

  // ❌ НЕ редиректим на публичных страницах
  const publicPaths = ['/', '/about', '/pricing', '/contact'];
  if (publicPaths.includes(pathname)) {
    return false;
  }

  // ❌ НЕ редиректим на страницах авторизации (они сами редиректят после успеха)
  const authPaths = ['/login', '/register', '/forgot-password', '/reset-password'];
  if (authPaths.some(p => pathname.startsWith(p))) {
    return false;
  }

  // ❌ НЕ редиректим на /auth-check - эта страница используется в iframe для проверки авторизации
  // с публичного домена markbase.ru и НЕ должна редиректить даже если нет токена
  if (pathname === '/auth-check' || pathname.startsWith('/auth-check')) {
    return false;
  }

  // ✅ Редиректим ТОЛЬКО если мы на app поддомене или в защищенных роутах
  if (hostname.startsWith('app.') || pathname.startsWith('/dashboard') ||
      pathname.startsWith('/products') || pathname.startsWith('/orders') ||
      pathname.startsWith('/suppliers') || pathname.startsWith('/warehouses') ||
      pathname.startsWith('/integrations') || pathname.startsWith('/marketplaces') ||
      pathname.startsWith('/connections') || pathname.startsWith('/api-keys') ||
      pathname.startsWith('/exchange-log') || pathname.startsWith('/api-integration') ||
      pathname.startsWith('/analytics') || pathname.startsWith('/sync') ||
      pathname.startsWith('/settings') || pathname.startsWith('/users') ||
      pathname.startsWith('/dictionaries') || pathname.startsWith('/admin') ||
      pathname.startsWith('/file-import') || pathname.startsWith('/automations') ||
      pathname.startsWith('/kassa')) {
    return true;
  }

  return false;
};

const getLoginUrl = () => {
  if (typeof window === 'undefined') return '/login';

  const hostname = window.location.hostname;
  const protocol = window.location.protocol;

  // На app.* или loopback — редирект на /login
  if (hostname.startsWith('app.') || isLoopbackHost(hostname)) {
    return '/login';
  }

  const appHostname = resolveMarkbaseProductionAppHostname(hostname);

  return `${protocol}//${appHostname}/login`;
};

// =====================================
// TRANSIENT RETRIES (backend warmup / temporary 5xx from proxy)
// =====================================
/**
 * GET к основному API безопасно ретраить при кратком 502/503/504 (прогрев backend, rolling deploy nginx).
 * Раньше был белый список — при массовых 502 весь shell сыпался, пока upstream снова не ответит.
 */
const isTransientGetRetryable = (url) => {
  const u = String(url || '');
  if (!u) return false;
  if (u.startsWith('/api/')) return true;
  if (u.includes('://') && /\/api\//.test(u)) return true;
  return false;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Один toast на серию 502/503/504 — иначе десятки всплывающих окон при падении upstream. */
const GATEWAY_ERROR_TOAST_COOLDOWN_MS = 12_000;
let lastGatewayErrorToastAt = 0;

const shouldShowDedupedGatewayToast = (status) => {
  if (status !== 502 && status !== 503 && status !== 504) return true;
  const now = Date.now();
  if (now - lastGatewayErrorToastAt < GATEWAY_ERROR_TOAST_COOLDOWN_MS) return false;
  lastGatewayErrorToastAt = now;
  return true;
};

const isGatewayStatus = (status) => status === 502 || status === 503 || status === 504;

const isOptionalMissingEndpoint = (url, status) => {
  if (status !== 404) return false;
  const u = String(url || '');
  return (
    u.includes('/api/mplaza/v1/agreements')
    || u.startsWith('/api/admin/public/')
    || u.startsWith('/api/admin/monitoring/')
    || u.startsWith('/api/admin/pricing-templates/global')
    || u.startsWith('/api/admin/pricing-rules/global')
    || u.startsWith(billingApiPath('resource-activation-status'))
    || /\/api\/suppliers\/[^/]+\/import-rules/.test(u)
    || /\/api\/suppliers\/[^/]+\/series(\?|$)/.test(u)
    || /\/api\/suppliers\/[^/]+\/products\/stats(\?|$)/.test(u)
    || /\/api\/suppliers\/[^/]+\/brands(\?|$)/.test(u)
    || /\/api\/prices\/display\//.test(u)
    || /\/api\/prices\/product\//.test(u)
  );
};

/** MPLAZA sidecar: shell poll of pending agreements. Down/502 must not toast on PIM pages. */
const isOptionalGatewayEndpoint = (url, status) => {
  if (!isGatewayStatus(status)) return false;
  return String(url || '').includes('/api/mplaza/v1/agreements');
};

const shouldSilenceGateway = (originalRequest, status) => {
  if (!isGatewayStatus(status)) return false;
  if (originalRequest?.silentGateway === true) return true;
  return isOptionalGatewayEndpoint(originalRequest?.url, status);
};

/** Background banners (billing/mplaza) must not toast «Ошибка сети» on 30s timeout. */
const shouldSilenceNetworkToast = (originalRequest) => {
  if (originalRequest?.silentNetwork === true) return true;
  const u = String(originalRequest?.url || '');
  return (
    u.startsWith('/api/billing/')
    || u.includes('/api/mplaza/v1/agreements')
  );
};

/** Shell-запросы до/во время checkAuth — 401 ожидаем, не показываем toast и не пугаем баннером. */
const isShellOptionalAuthEndpoint = (url) => {
  const u = String(url || '');
  return (
    u.includes('/api/uam/v1/unlock')
    || u.includes('/api/users/favorites/menu')
    || u.includes('/api/mplaza/v1/agreements')
    || u.includes('/api/concurens/v1/admin/access')
  );
};

const shouldSilenceShell401 = (originalRequest, status) => {
  if (status !== 401) return false;
  if (originalRequest?.silent401 === true) return true;
  const url = String(originalRequest?.url || '');
  return isShellOptionalAuthEndpoint(url) && !shouldRedirectToLogin();
};

/** Ожидаемые 401/404/gateway shell-запросов — без dev console.warn и без toast. */
const shouldSilenceExpectedHttpError = (originalRequest, status) => {
  if (originalRequest?.errorPresentation === 'inline') return true;
  if (shouldSilenceShell401(originalRequest, status)) return true;
  if (shouldSilenceGateway(originalRequest, status)) return true;
  if (status === 404) {
    if (originalRequest?.silent404 === true) return true;
    return isOptionalMissingEndpoint(originalRequest?.url, status);
  }
  return false;
};

/**
 * Дочерний axios с тем же origin и единым shell/toast-слоем (4.2), без refresh JWT и без GET-кэша основного instance.
 * Например: заголовок `X-Shop-Api-Key` для Shop integration API.
 */
export function createShellAwareAxiosInstance(mergeConfig = {}) {
  const { headers: extraHeaders, ...rest } = mergeConfig;
  const child = axios.create({
    baseURL: API_BASE_URL,
    timeout: REQUEST_TIMEOUT,
    withCredentials: true,
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...(extraHeaders || {}),
    },
  });

  attachHttpsPageOriginInterceptor(child);
  syncAxiosDefaultsBaseToHttps(child);

  child.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config || {};
    // Stale account lookups cannot clear a newer session or schedule retries.
    if (typeof originalRequest.isCurrentAuthRequest === 'function' && !originalRequest.isCurrentAuthRequest()) {
      return Promise.reject(error);
    }

      if (process.env.NODE_ENV === 'development' && !shouldSilenceExpectedHttpError(originalRequest, error.response?.status)) {
        // eslint-disable-next-line no-console
        console.warn(
          '❌ Ошибка HTTP (secondary client):',
          originalRequest?.method?.toUpperCase(),
          originalRequest?.url,
          error.response?.status,
          error.response?.data
        );
      }

      if (originalRequest.errorPresentation === 'inline') return Promise.reject(error);
      if (error.response) {
        const status = error.response.status;

        if (trySurfaceShellHttpError(error, { requestUrl: originalRequest?.url || '' })) {
          return Promise.reject(error);
        }

        const toastBody = getApiErrorToastBody(error);
        const shouldShowError =
          (status !== 401 || shouldRedirectToLogin())
          && !isOptionalMissingEndpoint(originalRequest?.url, status)
          && !shouldSilenceGateway(originalRequest, status);

        if (shouldShowError && status !== 401 && shouldShowDedupedGatewayToast(status)) {
          toastAPI.error(toastBody, 'Ошибка API');
        }
      } else if (error.request) {
        const networkFallback =
          error.code === 'ECONNABORTED'
            ? 'Превышено время ожидания ответа сервера. Проверьте соединение и повторите попытку.'
            : 'Проверьте соединение с интернетом';
        if (!shouldSilenceNetworkToast(originalRequest)) {
          toastAPI.error(
            pickSafeRussianBackendMessage(error?.message, networkFallback),
            'Ошибка сети'
          );
        }
      } else {
        toastAPI.error(
          pickSafeRussianBackendMessage(error?.message, 'Неизвестная ошибка'),
          'Ошибка выполнения'
        );
      }

      return Promise.reject(error);
    }
  );

  return child;
}

axiosInstance.interceptors.response.use(
  (response) => {
    // ✅ ВАЖНО: после любых мутаций (POST/PUT/PATCH/DELETE) очищаем GET-кэш,
    // но сохраняем справочники и статические данные (долгий кэш).
    // Регексы скомпилированы один раз на модульный scope (PRESERVE_AFTER_MUTATION_REGEX),
    // раньше пересоздавались на КАЖДЫЙ mutation-response (CPU spike при batch save'ах).
    try {
      const method = String(response?.config?.method || 'get').toLowerCase();
      if (method && method !== 'get') {
        axiosInstance.clearGetCache((key) => {
          const keyStr = String(key).toLowerCase();
          // false = preserve, true = clear
          if (PRESERVE_AFTER_MUTATION_REGEX.some((re) => re.test(keyStr))) {
            return false;
          }
          return true;
        });
      }
    } catch (_) {}

    if (
      process.env.NODE_ENV === 'development'
      && typeof window !== 'undefined'
      && window.__MB_AXIOS_TRACE__ === true
    ) {
      // eslint-disable-next-line no-console
      console.log(`✅ ${response.config.method?.toUpperCase()} ${response.config.url} ${response.status}`);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config || {};
    // Stale account lookups cannot clear a newer session or schedule retries.
    if (typeof originalRequest.isCurrentAuthRequest === 'function' && !originalRequest.isCurrentAuthRequest()) {
      return Promise.reject(error);
    }

    if (originalRequest?.url && (String(originalRequest.url).includes(API_ENDPOINTS.REFRESH) || String(originalRequest.url).endsWith(API_ENDPOINTS.REFRESH))) {
      clearTokens();
      // ✅ Редиректим на login ТОЛЬКО если это требуется
      if (typeof window !== 'undefined' && shouldRedirectToLogin()) {
        window.location.href = getLoginUrl();
      }
      return Promise.reject(error);
    }

    // ✅ Мягкий retry для публичных эндпоинтов при старте backend/nginx (не показываем notification)
    // Только GET и только на 502/503/504, чтобы не ретраить бизнес-ошибки и POST/PUT/DELETE.
    const status = error.response?.status;
    const method = String(originalRequest?.method || 'get').toLowerCase();
    if (
      originalRequest
      && method === 'get'
      && isTransientGetRetryable(originalRequest.url)
      && (status === 502 || status === 503 || status === 504)
    ) {
      const retryCount = Number(originalRequest._transientRetryCount || 0);
      // 3 попытки: ~0.3s + ~0.8s + ~1.5s = ~2.6s (хватает на прогрев контейнера)
      if (retryCount < 3) {
        originalRequest._transientRetryCount = retryCount + 1;
        const baseDelays = [300, 800, 1500];
        const jitter = Math.floor(Math.random() * 120); // небольшая рассинхронизация параллельных запросов
        await sleep(baseDelays[retryCount] + jitter);
        if (typeof originalRequest.isCurrentAuthRequest === 'function' && !originalRequest.isCurrentAuthRequest()) return Promise.reject(error);
        return axiosInstance(originalRequest);
      }
    }

    if (process.env.NODE_ENV === 'development' && !shouldSilenceExpectedHttpError(originalRequest, status)) {
      // eslint-disable-next-line no-console
      console.warn(
        '❌ Ошибка HTTP:',
        originalRequest?.method?.toUpperCase(),
        originalRequest?.url,
        error.response?.status,
        error.response?.data
      );
    }

    // ✅ КРИТИЧЕСКОЕ ИСПРАВЛЕНИЕ: Обработка 401 только если это реальная ошибка авторизации
    // Не обрабатываем сетевые ошибки или CORS ошибки как 401
    if (error.response?.status === 401 && !originalRequest._retry && error.response) {
      originalRequest._retry = true;
      const authErrorCode = error.response?.data?.code || error.response?.data?.error_code;
      const requestUrl = String(originalRequest?.url || '');
      const isAuthSessionCheck =
        requestUrl === API_ENDPOINTS.ME
        || requestUrl.endsWith(API_ENDPOINTS.ME)
        || ['NO_UAM_SESSION', 'USER_INACTIVE', 'SESSIONS_REVOKED', 'AUTH_REQUIRED', 'SESSION_EXPIRED', 'TOKEN_EXPIRED'].includes(authErrorCode);

      // После authReady 401 на защищённом API = потеря сессии.
      // Shell-optional (favorites/menu, silent401) — не убивают сессию: endpoint может
      // ответить 401 из-за моста/модуля при живом /api/auth/me.
      const softShell401 = shouldSilenceShell401(originalRequest, 401);
      const sessionLost = softShell401
        ? false
        : reportSessionUnauthorized({
          url: requestUrl,
          status: 401,
          code: authErrorCode,
        });

      // ── UAM-режим vs Legacy JWT ──
      // UAM: аутентификация через httpOnly cookie uam_session → refresh token отсутствует.
      //   Backend сам валидирует cookie через UamService (с graceful degradation/кэшем).
      //   Если backend вернул 401 — сессия действительно невалидна → очищаем кэш и редиректим.
      // Legacy JWT: refresh token есть в localStorage → пытаемся обновить access token.
      const refreshToken = getRefreshToken();

      if (refreshToken && !sessionLost) {
        // Legacy JWT mode — попытка обновления access token
        try {
          if (!refreshRequest) {
            refreshRequest = axios.post(`${API_BASE_URL}${API_ENDPOINTS.REFRESH}`, { refreshToken }, { headers: { 'Content-Type': 'application/json' } }).finally(() => { refreshRequest = null; });
          }
          const response = await refreshRequest;
          if (typeof originalRequest.isCurrentAuthRequest === 'function' && !originalRequest.isCurrentAuthRequest()) return Promise.reject(error);
          if (response.data && (response.data.success || response.status === 200)) {
            const newToken = response.data.token || response.data.data?.tokens?.accessToken;
            const newRefreshToken = response.data.refreshToken || response.data.data?.tokens?.refreshToken || refreshToken;
            setTokens(newToken, newRefreshToken);
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            const companyId = getTenantIdFromToken(newToken);
            if (companyId) originalRequest.headers['X-Tenant-ID'] = companyId;
            return axiosInstance(originalRequest);
          }
        } catch (refreshError) {
          if (typeof originalRequest.isCurrentAuthRequest === 'function' && !originalRequest.isCurrentAuthRequest()) return Promise.reject(refreshError);
          console.warn('Не удалось обновить токен:', refreshError);
          axiosInstance.clearGetCache(); // ✅ Очищаем кэш — данные для неавторизованного пользователя могут быть устаревшими
          clearTokens();
          reportSessionUnauthorized({ url: requestUrl, status: 401, code: 'TOKEN_REFRESH_FAILED' });
          if (typeof window !== 'undefined' && shouldRedirectToLogin() && isAuthSessionCheck) {
            window.location.href = getLoginUrl();
          }
          return Promise.reject(refreshError);
        }
      } else if (!refreshToken) {
        // ✅ UAM-режим: нет refresh token → cookie uam_session невалидна или истекла.
        if (sessionLost || isAuthSessionCheck) {
          axiosInstance.clearGetCache();
          clearTokens();
          // Редирект делает слушатель markbase_session_expired (AuthInitializer),
          // чтобы показать сообщение и очистить Redux один раз.
          if (isAuthSessionCheck && !sessionLost && typeof window !== 'undefined' && shouldRedirectToLogin()) {
            window.location.href = getLoginUrl();
          }
        }
      }
    }

    if (error.response) {
      const status = error.response.status;

      // 423 Locked — экран заблокирован, активируем блокировку на клиенте
      if (status === 423 && error.response?.data?.code === 'SCREEN_LOCKED') {
        try {
          localStorage.setItem('mb_screen_locked', 'true');
          window.dispatchEvent(new Event('markbase_screen_lock'));
          if (typeof BroadcastChannel !== 'undefined') {
            const ch = new BroadcastChannel('mb_screen_lock_channel');
            ch.postMessage({ type: 'LOCK', reason: 'server_423' });
            ch.close();
          }
        } catch {}
        return Promise.reject(error);
      }

      if (originalRequest.errorPresentation === 'inline') return Promise.reject(error);
      if (trySurfaceShellHttpError(error, { requestUrl: originalRequest?.url || '' })) {
        return Promise.reject(error);
      }

      const toastBody = getApiErrorToastBody(error);

      const shouldShowError =
        !shouldSilenceShell401(originalRequest, status)
        && (status !== 401 || shouldRedirectToLogin())
        && !isOptionalMissingEndpoint(originalRequest?.url, status)
        && !shouldSilenceGateway(originalRequest, status);

      if (shouldShowError && status !== 401 && shouldShowDedupedGatewayToast(status)) {
        toastAPI.error(toastBody, 'Ошибка API');
      }
    } else if (originalRequest.errorPresentation === 'inline') {
      return Promise.reject(error);
    } else if (error.request) {
      // FRONSYSTEM 4.1: без сырого англ. текста в UI; таймаут axios → отдельное русское сообщение
      const networkFallback =
        error.code === 'ECONNABORTED'
          ? 'Превышено время ожидания ответа сервера. Проверьте соединение и повторите попытку.'
          : 'Проверьте соединение с интернетом';
      if (!shouldSilenceNetworkToast(originalRequest)) {
        toastAPI.error(
          pickSafeRussianBackendMessage(error?.message, networkFallback),
          'Ошибка сети'
        );
      }
    } else {
      toastAPI.error(
        pickSafeRussianBackendMessage(error?.message, 'Неизвестная ошибка'),
        'Ошибка выполнения'
      );
    }

    return Promise.reject(error);
  }
);// =====================================
// EXTRA METHODS
// =====================================
axiosInstance.setToken = (token, refreshToken = null, options = {}) => setTokens(token, refreshToken, options);
axiosInstance.setSessionId = (sessionId, options = {}) => setSessionId(sessionId, options);
axiosInstance.clearTokens = () => clearTokens();
axiosInstance.getToken = () => getToken();
axiosInstance.getRefreshToken = () => getRefreshToken();
axiosInstance.getSessionId = () => getSessionId();
axiosInstance.isAuthenticated = () => {
  const token = getToken();
  return token && !isTokenExpired(token);
};
axiosInstance.getCurrentUser = () => decodeToken(getToken());
axiosInstance.createFileUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  const base = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
};

// =====================================
// ФОНОВЫЙ GC: чистим просроченные entries из responseCache.
// =====================================
// Без этого entries с TTL=5min висят в памяти 5 минут даже если их никто не
// запрашивает (eviction срабатывает только на set, после переполнения).
// На длинных сессиях (вкладка открыта весь день, навигация по разным разделам)
// это может удерживать десятки MB ответов API.
//
// Период GC в 60s — баланс между «слишком часто = шум» и «слишком редко = неэффективно».
// Использует requestIdleCallback при наличии (не блокирует main thread пока GC).
const RESPONSE_CACHE_GC_INTERVAL_MS = 60 * 1000;
let _responseCacheGcTimer = null;

const runResponseCacheGc = () => {
  try {
    const now = Date.now();
    let removed = 0;
    for (const [k, v] of responseCache) {
      const ttl = v?.ttlMs;
      if (ttl === Infinity) continue;
      const ttlNum = Number(ttl) || 0;
      if (ttlNum === 0) {
        // ttl=0 не должен попадать в кэш, но на всякий случай — чистим.
        responseCache.delete(k);
        removed += 1;
        continue;
      }
      const ts = Number(v?.ts) || 0;
      if (now - ts >= ttlNum) {
        responseCache.delete(k);
        removed += 1;
      }
    }
    // Заодно подметём «зависшие» inflight (cleanup в .then/.catch обычно срабатывает,
    // но при необработанных rejection или сетевых аномалиях бывают ostatки).
    for (const [k, v] of inflightRequests) {
      const startedAt = Number(v?.startedAt) || 0;
      if (startedAt && (now - startedAt) > INFLIGHT_STALE_MS) {
        inflightRequests.delete(k);
      }
    }
    if (process.env.NODE_ENV === 'development' && removed > 0) {
      // eslint-disable-next-line no-console
      console.debug(`[axios.js GC] purged ${removed} stale entries; cache=${responseCache.size}, inflight=${inflightRequests.size}`);
    }
  } catch (_) { /* GC не должен ломать приложение */ }
};

const scheduleGc = () => {
  // requestIdleCallback недоступен в Safari/iOS — fallback на чистый таймер.
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    try {
      window.requestIdleCallback(runResponseCacheGc, { timeout: 5000 });
      return;
    } catch (_) { /* fallthrough */ }
  }
  runResponseCacheGc();
};

if (typeof window !== 'undefined') {
  // Не запускаем GC в SSR (window отсутствует).
  _responseCacheGcTimer = setInterval(scheduleGc, RESPONSE_CACHE_GC_INTERVAL_MS);
  // unref не существует в браузере (это Node); в браузере setInterval не блокирует unload.
  try {
    window.addEventListener('beforeunload', () => {
      if (_responseCacheGcTimer) {
        clearInterval(_responseCacheGcTimer);
        _responseCacheGcTimer = null;
      }
      // На beforeunload явно опустошаем кэш — освободит память до полного teardown.
      responseCache.clear();
      inflightRequests.clear();
    }, { once: true });
  } catch (_) { /* noop */ }
}

// Экспорт для unit-тестов / явного teardown (например, в Storybook).
axiosInstance.__stopResponseCacheGc = () => {
  if (_responseCacheGcTimer) {
    clearInterval(_responseCacheGcTimer);
    _responseCacheGcTimer = null;
  }
};

export default axiosInstance;